import { MouseEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useToggle from "../../hooks/useToggle";
import SvgEdit from "../icons/Edit";
import Icon from "./Icon";
import styles from "./InlineBadgeEditor.module.css";
import { Menu, MenuItem } from "./Menu";
import StatusBadge, { StatusBadgeColor } from "./StatusBadge";

export type InlineBadgeEditorOption<T extends string = string> = {
  value: T;
  label: string;
  color: StatusBadgeColor;
};

export type InlineBadgeEditorProps<T extends string = string> = {
  value?: T;
  options: InlineBadgeEditorOption<T>[];
  onChange: (value: T) => void;
};

const InlineBadgeEditor = <T extends string = string>({ value, options, onChange }: InlineBadgeEditorProps<T>) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menu = useToggle();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useOnClickOutside(menuRef, menu.close);

  useEffect(() => {
    if (!menu.isOpen) return;

    window.addEventListener("scroll", menu.close, true);
    window.addEventListener("resize", menu.close);

    return () => {
      window.removeEventListener("scroll", menu.close, true);
      window.removeEventListener("resize", menu.close);
    };
  }, [menu.isOpen]);

  // Position is set as soon as the menu opens (using the trigger's rect), then corrected once the menu itself is
  // measured below, so it never renders past the viewport's right or bottom edge.
  useLayoutEffect(() => {
    if (!menu.isOpen || !triggerRef.current || !menuRef.current) return;

    const margin = 8;
    const trigger = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();

    let left = trigger.left;
    if (left + menuRect.width > window.innerWidth - margin) {
      left = window.innerWidth - menuRect.width - margin;
    }
    left = Math.max(left, margin);

    let top = trigger.bottom + 4;
    if (top + menuRect.height > window.innerHeight - margin) {
      top = trigger.top - menuRect.height - 4;
    }
    top = Math.max(top, margin);

    setPosition((current) => (current?.top === top && current?.left === left ? current : { top, left }));
  }, [menu.isOpen]);

  const currentOption = options.find((option) => option.value === value);

  const handleTriggerClick = (e: MouseEvent) => {
    e.stopPropagation();

    if (!menu.isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left });
    }

    menu.toggle();
  };

  const handleSelect = (nextValue: T) => {
    onChange(nextValue);
    menu.close();
  };

  return (
    <div ref={triggerRef} className={styles.wrapper}>
      <div className={styles.trigger} onClick={handleTriggerClick}>
        {currentOption && <StatusBadge color={currentOption.color}>{currentOption.label}</StatusBadge>}
        <span className={styles.editIcon}>
          <Icon glyph={SvgEdit} />
        </span>
      </div>

      {menu.isOpen &&
        position &&
        createPortal(
          <Menu
            ref={menuRef}
            accentColor="var(--red)"
            className={styles.menu}
            style={{ top: position.top, left: position.left }}
          >
            {options
              .filter((option) => option.value !== value)
              .map((option) => (
                <MenuItem key={option.value} label={option.label} onClick={() => handleSelect(option.value)} />
              ))}
          </Menu>,
          document.body,
        )}
    </div>
  );
};

export default InlineBadgeEditor;
